import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/UnitList.css"; // Assuming you want to reuse the same stylesheet

const DepartmentCustomerList = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [deleteModal, setDeleteModal] = useState({ show: false, registration: null });
  const API_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    // Ensure you have REACT_APP_API_URL in your .env file
    const apiUrl = `${API_URL}list-department-reg.php`;
    fetch(apiUrl)
      .then((res) => res.json())
      .then((response) => {
        if (response.status === "success" && Array.isArray(response.data)) {
          setRegistrations(response.data);
        } else {
          showToast("Failed to load department registrations", "error");
        }
      })
      .catch(() => showToast("Error fetching data from the server", "error"));
  }, [API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const filteredRegistrations = registrations.filter((reg) => {
    const term = searchTerm.toLowerCase();
    return (
      reg.dept_name.toLowerCase().includes(term) ||
      reg.reg_no.toLowerCase().includes(term)
    );
  });

  const handleEdit = (reg) => {
    // Navigate to the add page with edit data
    navigate("/add-department-customer", { state: { registration: reg, isEditing: true } });
    showToast(`Editing registration: ${reg.reg_no}`, "info");
  };

  const handleDelete = (reg) => {
    setDeleteModal({ show: true, registration: reg });
  };

  const confirmDelete = async () => {
    const { registration } = deleteModal;
    try {
      const response = await fetch(`${API_URL}delete-department-reg.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: registration.id }),
      });

      const data = await response.json();

      if (data.message && data.message.includes("deleted successfully")) {
        showToast(
          `Registration "${registration.reg_no}" deleted successfully`,
          "success"
        );
        setRegistrations((prevRegistrations) =>
          prevRegistrations.filter((reg) => reg.id !== registration.id)
        );
      } else if (data.status === "success") {
        // Fallback for APIs that use status field
        showToast(
          `Registration "${registration.reg_no}" deleted successfully`,
          "success"
        );
        setRegistrations((prevRegistrations) =>
          prevRegistrations.filter((reg) => reg.id !== registration.id)
        );
      } else {
        showToast(data.message || "Failed to delete registration", "error");
      }
    } catch (error) {
      showToast("Network error. Could not delete registration.", "error");
    }
    setDeleteModal({ show: false, registration: null });
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, registration: null });
  };

  const handleAdd = () => {
    navigate("/add-department-customer");
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Department Registrations</h1>
          <p>Manage department customer registrations</p>
        </div>
        <button className="add-unit-button" onClick={handleAdd}>
          + Add New Registration
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by department or registration no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Department Name</th>
              <th>Registration No.</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRegistrations.map((reg) => (
              <tr key={reg.id}>
                <td>
                  <div className="unit-info">
                    <div className="unit-icon">🏢</div>
                    <span>{reg.dept_name}</span>
                  </div>
                </td>
                <td>{reg.reg_no}</td>
                <td>{new Date(reg.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(reg)}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(reg)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredRegistrations.length} of {registrations.length}{" "}
          registrations
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the registration{" "}
                <strong>"{deleteModal.registration?.reg_no}"</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                No, Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentCustomerList;
